import store from '@/store';
import { isExternal } from '@/utils/validate';
import MesLayout from '@/components/layout';
import Cookie from '@/utils/cookie';
import MesRouterView from '@/components/layout/components/router-view';

/**
 * 生成前台路由
 * @param {Array} data 菜单列表
 * @param {Function} next
 */
export function addHomeRouter(data) {
  const router = [{
    path: '/home',
    redirect: '/home/index',
    component: MesLayout,
    children: [{
      path: '/home/index',
      name: 'index',
      component: () => import('@/views/home/index/index')
    },
    {
      path: '/home/login',
      name: 'login',
      component: () => import('@/views/home/login/index.vue')
    }]
  }];
  try {
    data.forEach(item => {
      // 判断是否未内部url地址
      if (!isExternal(item.menuUrl)) {
        router[0].children.push({
          path: item.menuUrl,
          component: () => import(`@/views/home/${item.menuUrl}`),
          meta: {
            title: (Cookie.get('lanConfig') === 'en' ? item.enMenuName : item.menuName)
          }
        });
      } else {
        const origin = location.origin;
        const { menuUrl, menuName, enMenuName } = item;
        if (menuUrl.includes(origin)) { // 外链地址为本系统地址
          const path = menuUrl.replace(origin, '');
          router.push({
            path,
            component: () => import(`@/views${path}`),
            meta: {
              title: (Cookie.get('lanConfig') === 'en' ? enMenuName : menuName)
            }
          });
        }
      }
    });
  } catch (e) {
    return [];
  }
  return router;
}

/**
 * 生成后台路由
 * @param {Array} routerList 菜单列表
 * @param {String} parentRouter 父路由路径
 * @param {Boolean} isChildren 是否是 children
 */
export function addRouter(routerList, parentRouter = '', isChildren = false) {
  const router = [];
  const readOnly = [];
  try {
    routerList.forEach(item => {
      // 是否是外链
      const isExternalLink = isExternal(item.menuUrl);
      // 重定向路由对象
      let redirect = null;
      if (!isExternalLink && item.children) {
        redirect = item.children.find(v => {
          return v.isShowMenu;
        });
      }

      const path = getPath(item, parentRouter, isExternalLink);
      const index = router.findIndex(v => v.path === path);
      /* TODO: 后台无法控制页面功能权限，采用不同权限进入不同菜单暂时解决（菜单的地址相同，需要去重），后面再看看怎么优化*/
      if (index === -1) {
        let obj = {
          path,
          name: getName(item.menuUrl, isExternalLink),
          component: getComponent(item, isChildren, isExternalLink, redirect),
          meta: {
            title: (Cookie.get('lanConfig') === 'en' ? item.enMenuName : item.menuName)
          }
        };

        // 重定向
        if (redirect) {
          obj = { ...obj, redirect: redirect.menuUrl };
        }

        // 有 children
        if (item.children) {
          const children = addRouter(item.children, item.menuUrl + '/', true);
          obj = { ...obj, children };
        }

        // 有 icon
        if (item.icon) {
          obj.meta.icon = item.icon;
        }

        // 新窗口打开
        if (item.isOpen) {
          obj.meta.open = item.isOpen;
        }

        // 总是显示
        const { isShowMenu, parameter } = item;
        if (!isShowMenu || (parameter && parameter.includes('alwaysShow'))) {
          obj.alwaysShow = true;
        }

        if (parameter) {
          // 总是显示
          if (parameter === 'alwaysShow') {
            obj.alwaysShow = true;
          }

          // 定检单页面操作权限
          if (parameter.includes('reset')) { // 录入、上传、复位
            obj.meta.roles = ['input', 'upload', 'reset'];
          } else if (parameter.includes('upload')) { // 录入、上传
            obj.meta.roles = ['input', 'upload'];
          }
        }

        // 不显示在侧边栏
        if (!item.isShowMenu) {
          obj.hidden = !item.isShowMenu;
          obj.meta.activeMenu = getActiveMenu(item.menuUrl);
        }

        // 外链
        if (isExternalLink) {
          let redirect = null;
          // 外链有 children
          if (item.children) {
            redirect = item.children.find(v => {
              return !v.isOpen;
            });
          } else {
            const children = externalLink(item);
            obj = { ...obj, children };
          }

          // 外链重定向
          if (redirect) {
            const { children, menuUrl, id } = redirect;
            const url = !children ? `?redirect=${encodeURIComponent(menuUrl)}` : '';
            obj.redirect = `/iframe-${id}/${id}${url}`;
          }
        }
        router.push(obj);
      }
    });
  } catch (e) {
    return [];
  }
  store.dispatch('setReadOnly', readOnly);
  return router;
}

/**
 * 获取路径
 * @param {Object} data 路由对象
 * @param {String} parentRouter 父路由路径
 * @param {Boolean} isExternalLink 是否是外链
 * @return {String} 路径
 */
function getPath(data, parentRouter, isExternalLink) {
  return isExternalLink ? `/iframe-${data.id}` : data.menuUrl.replace(parentRouter, '');
}

/**
 * 获取命名路由
 * @param {String} router 路径地址
 * @param {Boolean} isExternalLink 是否是外链
 * @return {String} 命名路由
 */
function getName(router, isExternalLink) {
  if (isExternalLink) { // 外链
    return '';
  }
  const path = getRouterPath(router);
  return path.replace(/(\/|-)(\w)/g, (all, symbol, letter) => {
    return letter.toUpperCase();
  });
}

/**
 * 获取组件
 * @param {Object} data 路由对象
 * @param {Boolean} isChildren 是否是 children
 * @param {Boolean} isExternalLink 是否是外链
 * @param {Object} redirect 重定向路由对象
 * @return {Object|Function} 组件
 */
function getComponent(data, isChildren, isExternalLink, redirect) {
  return !isChildren ? MesLayout : isExternalLink || (data.children && redirect)
    ? MesRouterView : () => import(`@/views${getRouterPath(data.menuUrl)}`);
}

/**
 * 获取路由地址（去掉路由地址动态路由匹配）
 * @param {String} router 路由地址
 * @return {String} 路由地址
 */
function getRouterPath(router) {
  const reg = /\/:\w+/g;
  if (reg.test(router)) {
    return router.replace(reg, '');
  }
  return router;
}

/**
 * 外链
 * @param {Object} data 菜单对象
 * @return {Array} 路由列表
 */
function externalLink(data) {
  const children = [];
  if (data.isOpen) { // 新窗口打开
    children.push({
      path: data.menuUrl,
      meta: {
        title: (Cookie.get('lanConfig') === 'en' ? data.enMenuName : data.menuName)
      }
    });
  } else { // 内联
    const obj = {
      path: data.id,
      component: () => import('@/components/layout/iframe'),
      meta: {
        title: (Cookie.get('lanConfig') === 'en' ? data.enMenuName : data.menuName),
        redirect: data.menuUrl,
        breadcrumb: false
      }
    };
    children.push(obj);
  }
  return children;
}

/**
 * 侧边栏高亮
 * @param {String} path 路径地址
 * @return {String} 活动菜单地址
 */
// eslint-disable-next-line no-unused-vars
function getActiveMenu(path) {
  path = path.replace(/\/[\w-]+\/:.+|\/[\w-]+$/, '');
  const temp = path.substring(1).split('/');
  if (temp.length > 3) {
    temp.length = 3;
    path = temp.reduce((total, value) => {
      return total + '/' + value;
    }, '');
  }
  return path;
}